import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import VenueList from './VenueList';
import VenueForm from './VenueForm';
import VenueDashboard from './VenueDashboard';
import VenueScopedLayout from '../../../components/layout/VenueScopedLayout';
import VenueAccessGuard from '../../../components/auth/VenueAccessGuard';
import FullAdminGuard from '../../../components/auth/FullAdminGuard';
// Locations
import VenueLocationList from './locations/VenueLocationList';
import VenueLocationForm from './locations/VenueLocationForm';
// Wine Lists
import WineListList from './wine-lists/WineListList';
import WineListForm from './wine-lists/WineListForm';
import WineListDetail from './wine-lists/WineListDetail';
import WineListItemForm from './wine-lists/WineListItemForm';
import WineListImport from './wine-lists/WineListImport';
// Menus (Phase 2B — cocktail-only)
import MenuList from './menus/MenuList';
import MenuForm from './menus/MenuForm';
import MenuDetail from './menus/MenuDetail';
import MenuItemForm from './menus/MenuItemForm';
// Menu Consumption Recipes (Phase 2B — INERT storage)
import MenuConsumptionRecipeList from './menu-consumption-recipes/MenuConsumptionRecipeList';
import MenuConsumptionRecipeForm from './menu-consumption-recipes/MenuConsumptionRecipeForm';
// Inventory
import InventoryDashboard from './inventory/InventoryDashboard';
import InventoryMovementFeed from './inventory/InventoryMovementFeed';
import InventoryValuation from './inventory/InventoryValuation';
import InventoryItemList from './inventory/InventoryItemList';
import InventoryItemDetail from './inventory/InventoryItemDetail';
import InventoryItemForm from './inventory/InventoryItemForm';
import ManualAdjustmentForm from './inventory/ManualAdjustmentForm';
import InventoryAlerts from './inventory/InventoryAlerts';
// Purchasing
import PurchaseOrderList from './purchasing/PurchaseOrderList';
import PurchaseOrderForm from './purchasing/PurchaseOrderForm';
import PurchaseOrderDetail from './purchasing/PurchaseOrderDetail';
import ReceivingScreen from './purchasing/ReceivingScreen';
import InvoiceList from './purchasing/InvoiceList';
// Stock Counts
import StockCountList from './stock-counts/StockCountList';
import StockCountForm from './stock-counts/StockCountForm';
import StockCountEntry from './stock-counts/StockCountEntry';
import StockCountReview from './stock-counts/StockCountReview';
// Transfers
import TransferList from './transfers/TransferList';
import TransferForm from './transfers/TransferForm';
import TransferEntry from './transfers/TransferEntry';
import TransferReview from './transfers/TransferReview';
// Sales Imports (Phase 5.A.4)
import SalesImportList from './sales-imports/SalesImportList';
import SalesImportUpload from './sales-imports/SalesImportUpload';
import SalesImportDetail from './sales-imports/SalesImportDetail';
// POS Inventory
import PosGovernanceList from './pos-inventory/PosGovernanceList';
// Recipes
import RecipeList from './recipes/RecipeList';
import RecipeDetail from './recipes/RecipeDetail';
// Production Orders
import ProductionOrderList from './production-orders/ProductionOrderList';
import ProductionOrderCreate from './production-orders/ProductionOrderCreate';
import ProductionOrderDetail from './production-orders/ProductionOrderDetail';
// Ownership
import OwnershipAccountList from './ownership/OwnershipAccountList';
import OwnershipAccountForm from './ownership/OwnershipAccountForm';
import OwnershipReports from './ownership/OwnershipReports';
// Suppliers (venue-scoped)
import VenueSupplierList from './suppliers/VenueSupplierList';
import VenueSupplierDetail from './suppliers/VenueSupplierDetail';

const VenueRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Global venue management — admin-only. VENUE_USER hitting /admin/venues
          is bounced into their assigned venue (or the picker) by FullAdminGuard. */}
      <Route element={<FullAdminGuard />}>
        <Route path="/" element={<VenueList />} />
        <Route path="/create" element={<VenueForm />} />
        <Route path="/edit/:venueId" element={<VenueForm />} />
      </Route>

      {/* Venue-scoped workspace — VenueAccessGuard reads :venueId and blocks
          venue users who try to reach a venue they aren't assigned to. */}
      <Route path="/:venueId" element={<VenueAccessGuard />}>
        <Route element={<VenueScopedLayout />}>
          <Route index element={<VenueDashboard />} />

        {/* Locations */}
        <Route path="locations" element={<VenueLocationList />} />
        <Route path="locations/create" element={<VenueLocationForm />} />
        <Route path="locations/edit/:locationId" element={<VenueLocationForm />} />

        {/* Wine Lists — LEGACY COMPATIBILITY SURFACE.
            Primary workflow now lives under Menus. WineListList is gated as
            a soft-redirect surface (Phase 4.I) and these routes remain only
            so bookmarked URLs and the migration window stay functional. */}
        <Route path="wine-lists" element={<WineListList />} />
        <Route path="wine-lists/create" element={<WineListForm />} />
        <Route path="wine-lists/edit/:wineListId" element={<WineListForm />} />
        <Route path="wine-lists/:wineListId" element={<WineListDetail />} />
        <Route path="wine-lists/:wineListId/add" element={<WineListItemForm />} />
        <Route path="wine-lists/:wineListId/edit/:itemId" element={<WineListItemForm />} />
        <Route path="wine-lists/:wineListId/import" element={<WineListImport />} />

        {/* Menus — canonical menu-family surface (Phase 4.B, 4.H, 4.J).
            Supports kind='COCKTAIL' and kind='WINE' items end-to-end. */}
        <Route path="menus" element={<MenuList />} />
        <Route path="menus/create" element={<MenuForm />} />
        <Route path="menus/edit/:menuId" element={<MenuForm />} />
        <Route path="menus/:menuId" element={<MenuDetail />} />
        <Route path="menus/:menuId/add" element={<MenuItemForm />} />
        <Route path="menus/:menuId/edit/:itemId" element={<MenuItemForm />} />

        {/* Menu Consumption Recipes — LEGACY COMPATIBILITY SURFACE.
            Primary workflow now lives under Menus > Recipes tab (Phase 4.J).
            These routes back the standalone edit pages and remain mounted
            because MenuRecipesPanel deep-links to them for create/edit. */}
        <Route path="menu-consumption-recipes" element={<MenuConsumptionRecipeList />} />
        <Route path="menu-consumption-recipes/create" element={<MenuConsumptionRecipeForm />} />
        <Route path="menu-consumption-recipes/edit/:recipeId" element={<MenuConsumptionRecipeForm />} />

        {/* Inventory */}
        <Route path="inventory" element={<InventoryItemList />} />
        <Route path="inventory/dashboard" element={<InventoryDashboard />} />
        <Route path="inventory/movements" element={<InventoryMovementFeed />} />
        <Route path="inventory/valuation" element={<InventoryValuation />} />
        <Route path="inventory/create" element={<InventoryItemForm />} />
        <Route path="inventory/adjust" element={<ManualAdjustmentForm />} />
        <Route path="inventory/alerts" element={<InventoryAlerts />} />
        <Route path="inventory/:itemId" element={<InventoryItemDetail />} />
        <Route path="inventory/:itemId/edit" element={<InventoryItemForm />} />

        {/* Purchasing */}
        <Route path="purchasing" element={<PurchaseOrderList />} />
        <Route path="purchasing/invoices" element={<InvoiceList />} />
        <Route path="purchasing/create" element={<PurchaseOrderForm />} />
        <Route path="purchasing/:poId" element={<PurchaseOrderDetail />} />
        <Route path="purchasing/:poId/edit" element={<PurchaseOrderForm />} />
        <Route path="purchasing/:poId/receive" element={<ReceivingScreen />} />

        {/* Stock Counts */}
        <Route path="stock-counts" element={<StockCountList />} />
        <Route path="stock-counts/create" element={<StockCountForm />} />
        <Route path="stock-counts/:countId" element={<StockCountEntry />} />
        <Route path="stock-counts/:countId/review" element={<StockCountReview />} />

        {/* Transfers */}
        <Route path="transfers" element={<TransferList />} />
        <Route path="transfers/create" element={<TransferForm />} />
        <Route path="transfers/:transferId" element={<TransferEntry />} />
        <Route path="transfers/:transferId/review" element={<TransferReview />} />

        {/* Sales Imports (Phase 5.A.4) — operational document workflow that
            generates SALE InventoryMovements. Not a movement itself. */}
        <Route path="sales-imports" element={<SalesImportList />} />
        <Route path="sales-imports/import" element={<SalesImportUpload />} />
        <Route path="sales-imports/:importId" element={<SalesImportDetail />} />

        {/* POS Inventory — backend gates this admin-only; mirror it on the
            frontend so VENUE_USER doesn't reach a page they can't load. */}
        <Route element={<FullAdminGuard />}>
          <Route path="pos-inventory/governance" element={<PosGovernanceList />} />
        </Route>

        {/* Production Recipes */}
        <Route path="recipes" element={<RecipeList />} />
        <Route path="recipes/create" element={<RecipeDetail />} />
        <Route path="recipes/:recipeId" element={<RecipeDetail />} />

        {/* Production Orders */}
        <Route path="production-orders" element={<ProductionOrderList />} />
        <Route path="production-orders/create" element={<ProductionOrderCreate />} />
        <Route path="production-orders/:orderId" element={<ProductionOrderDetail />} />

        {/* Ownership */}
        <Route path="ownership-accounts" element={<OwnershipAccountList />} />
        <Route path="ownership-accounts/create" element={<OwnershipAccountForm />} />
        <Route path="ownership-accounts/edit/:accountId" element={<OwnershipAccountForm />} />
        <Route path="ownership-reports" element={<OwnershipReports />} />

        {/* Suppliers (venue-scoped, read-only) */}
        <Route path="suppliers" element={<VenueSupplierList />} />
        <Route path="suppliers/:supplierId" element={<VenueSupplierDetail />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/admin/venues" replace />} />
    </Routes>
  );
};

export default VenueRoutes;
